import type { 
    OrderByDirection, WhereFilterOp, Timestamp
} from '@angular/fire/firestore';
export type TTimestamp = Timestamp;

export type TApp = 'app' | 'backend' | undefined;
export interface IAccount {
    accountNumber: string;
    affiliateId?: string;
    dealershipName?: string;    // dealershipName is only used in Account TOC
    signupCouponCode?: string;
    uid?: string;
}
export type TAccount = IAccount;

export interface IAccountTOC {
    items: TAccountItem[];
}
export type TAccountTOC = IAccountTOC;

export interface IAccountItem {
    accountNumber: string;
    dealershipName?: string;
    uid: string;
}
export type TAccountItem = IAccountItem;

/*
export interface ICreateAccountInfoOld {
    // Manager Info
    'firstName': string;
    'lastName': string;
    'cellPhone': string;
    'email': string;
    'userUid': string;
    'password'?: string;
    'roleTitle': string;

    // 'value': this.utilsService.getRoleTitle(info.role)
    // 'value': info.packageName
    // 'value': this.dateService.formatTimestamp(info.expirationDate, DateTimeFormats.ShortDate)


    // Dealership Info
    'dealership': TDealership;  // added 9/1/24.

    'dealershipName': string;
    'phoneNumber': string;

    'countryAbbr': string;
    'countryName': string;
    'countryCode': string;
    'isoCountryCode': string;
    'isoRegionCode': string;
    'isoRegionName': string;

    'timezone': string;
    'phonePrefixes': string[];
    'fullAddress': string;
    'streetAddress': string;
    'zipcode': string;
    'city': string;
    'coordinate': {
        latitude: number;
        longitude: number;
    };

    // Other Info
    'packageName': TPackageName;
    'expirationDate': TTimestamp;
    'accountNumber': string;
    'affiliateId': string;
    'signupCouponCode': string;
    'lists': string[];
    'tags': string[];
}
export type TCreateAccountInfo = ICreateAccountInfo;
*/

export interface ICreateAccountResponseInfo {
    'expirationDate': TTimestamp;
    'accountNumber': string;
    'affiliateId': string;
    'signupCouponCode': string;
    'lists': string[];
    'tags': string[];
    'password': string;
    'claims': string;
    'packageName': TPackageName;
}
export type TCreateAccountResponseInfo = ICreateAccountResponseInfo;

export interface IAccountCreationQuestionsAndAnswers {
    hasReliableInternet?: boolean;
    locatedInUnitedStates?: boolean;
    isServiceManager?: boolean;
    acceptsTrialTerms?: boolean;
    acceptsLearningCurve?: boolean;
    commitsToOnboarding?: boolean;
}
export type TAccountCreationQuestionsAndAnswers = IAccountCreationQuestionsAndAnswers;

export interface ICreateAccountInfo {
    // Manager Info
    'user': TUser;
    'questionsAndAnswers': TAccountCreationQuestionsAndAnswers;

    // Other Info - This data will be returned in the response.
    'accountNumber'?: string;
    'password'?: string;
    'packageName'?: TPackageName;
    'expirationDate'?: TTimestamp;
    'affiliateId'?: string;
    'signupCouponCode'?: string;
    'lists'?: string[];
    'tags'?: string[];

    // response: TCreateAccountResponseInfo;
}
export type TCreateAccountInfo = ICreateAccountInfo;

export interface IAccountProfile {
    accountNumber: string;
    activationInfo: TActivationInfo;
    ambassadorInfo?: TAmbassadorInfo;
    creditInfo?: TCreditInfo;       // Made optional on 1/23/2026
    debug?: boolean;
    featuresInfo: TFeaturesInfo;
    fromSmsNumber?: string;
    modifiedDate?: TTimestamp;
    notes?: string;
    packageName: TPackageName;
    periodInfo: TPeriodInfo;
    phoneNumber?: string;
    showSetUserButton?: boolean;
    pricingInfo: TPricingInfo;
    signupDate: TTimestamp;
    sku?: string;
    status: TAccountStatus;
    type?: TPackageType;
    uid?: string;
}
export type TAccountProfile = IAccountProfile;

export type TAccountStatus = 'Active' | 'In Trial Period' | 'Trial Period Expired' |
    'Cancelled By Support' | 'Cancelled By Customer' | 'Package Expired';

export interface IAddress {
    city: string;
    coordinate: TCoordinate;
    countryAbbr?: string;
    countryName: string;
    fullAddress?: string;
    isoCountryCode?: string;     // This was required - made optional on 11/18/2025
    // isoRegionCode: string;
    regionName: string;
    regionCode?: string;
    streetAddress: string;
    zipcode: string;
}
export type TAddress = IAddress;

type TAddressPartial = Pick<TAddress, 'city' | 'coordinate' |  'regionName' | 'streetAddress' | 'zipcode'>;

export interface ICreditInfo {
    availableCredits: number;
    lastCreditPurchaseDate?: TTimestamp;
}
export type TCreditInfo = ICreditInfo;

export interface IPricingInfo {
    priceName?: string;
    status?: TPriceStatus;
    paygCreditsCostPerCustomerMsg?: number;
    paygCreditsCostPerCampaignMsg?: number;
    paygBasePrice?: number;
    unlimitedMonthlyBasePrice?: number;
    unlimitedYearlyBasePrice?: number;
    ambassadorYearlyBasePrice?: number;
    uid?: string;
}
export type TPricingInfo = IPricingInfo;
export type TPriceStatus = 'Active' | 'Non-Active';

// Define array first with 'as const'
export const PackageTypes = [
    'Activation',
    'Pay As You Go',
    'Monthly',
    'Yearly',
    'Ambassador',
    'Trial',
    'Optional Service'
] as const;

// Derive the type from the array
export type TPackageType = typeof PackageTypes[number];

// export type TPackageType = 'Activation' |'Pay As You Go' | 'Monthly' | 'Yearly' | 'Ambassador' | 'Trial' | 'Optional Service';

export type TPackageName = 'Trial Package' | 'Pay As You Go Package' |
    'Unlimited Monthly Package' | 'Unlimited Yearly Package' | 'Ambassador Package';

export interface IActivationInfo {
    activationFeeOrderNumber?: number;
    activationDate: TTimestamp;
}
export type TActivationInfo = IActivationInfo;

export interface IAlertResponse {
    'selection': boolean;
    // 'selectedValues'?: string[] | null;
    'value': string | string[] | null;
    // 'values:'?: string[] | null;
}
export type TAlertResponse = IAlertResponse;

export interface IAmbassadorInfo {
    startDate?: TTimestamp;
    endDate?: TTimestamp;
}
export type TAmbassadorInfo = IAmbassadorInfo;

export interface IPeriodInfo {
    startDate: TTimestamp;
    expirationDate: TTimestamp;
    premiumSupportExpirationDate: TTimestamp;
    webHostingExpirationDate: TTimestamp;
}
export type TPeriodInfo = IPeriodInfo;

export interface IFeaturesInfo {
    premiumSupport?: boolean;
    webHosting?: boolean;
    lockedInPricing?: boolean;
    campaign?: boolean;
    customer?: boolean;
    efficiency?: boolean;
}
export type TFeaturesInfo = IFeaturesInfo;

export type TAnyValue = string | number | object;

export interface IBackupFilename {
    fileName: string;
}
export type TBackupFilename = IBackupFilename;

export interface ICampaign {
    createdDate: TTimestamp;
    sentDate: TTimestamp;
    numRecipients: number;
    message: string;
    uid: string;
}
export type TCampaign = ICampaign;

// The TTimestamp declared in functions is different than the TTimestamp declared in the App.
// In functions this is legal:
//    const ts = this.dateService.now();
//    ts.toDate();
// However it is not legal in the App.
// The obvious solution is to use only a single definition of what a TTimestamp is.
// So, I removed the vanilla TTimestamp and replaced it with the firestore.Timestamp.
// Let's see if that works!
//
// import {Timestamp } from '@firebase/firestore-types';    // commented out on 12/2/2022
// export type TTimestamp = Timestamp;                      // commented out on 12/2/2022
// export type TTimestamp = firestore.Timestamp;               // inserted on 12/2/2022

export interface ICampaignIncentive {
    title: string;
    line1?: string;
    line2?: string;
    line3?: string;
    line4?: string;
    line5?: string;
    line6?: string;
    line7?: string;
    note?: string;
    dateRange?: string;
    value?: string;
    incentiveCode?: string;
    buttonText?: string;
    showCallButton?: boolean;
    sequenceNumber?: number;    // Remove this
    uid: string;
}
export type TCampaignIncentive = ICampaignIncentive;

export interface ICampaignIncentiveSet {
    setName: string;
    incentiveSetId: number;
    incentiveUids: string[];
    uid: string;
}
export type TCampaignIncentiveSet = ICampaignIncentiveSet;

export interface ICampaignOptions {
    customerName?: string;
}
export type TCampaignOptions = ICampaignOptions;
export interface IDailyValueUids {
    uids: string[];
    dailyValues: TDailyValue[]; // Replace TDailyValue with the actual type of dailyValues
}
export type TDailyValueUids = IDailyValueUids;

export interface IChannel {
    deploymentChannelName?: string;
    downloadProgress?: number;
    isLoading?: boolean;
    loadChannel?: string;
}
export type TChannel = IChannel;

export interface IClaim {
    accountNumber: string;
    role: string;
    userUid: string;
}
export type TClaim = IClaim;
export interface ICoordinate {
    latitude: number;
    longitude: number;
}
export type TCoordinate = ICoordinate;

export interface ICoordinateExt {
    latitude?: number;
    longitude?: number;
    locationName?: string;
    letter?: string;
}
export type TCoordinateExt = ICoordinateExt;

export interface ICountry {
    countryAbbr: string;
    countryCode: string;
    countryName: string;
    isoCountryCode: string;
    uid: string;
}
export type TCountry = ICountry;

export interface ICustomer {
    cellPhone: string;
    cellPhoneStr?: string;
    countryCode?: string;
    maskedCellPhone?: string;
    customerName: string;
    createdDate?: TTimestamp;
    isShuttlePassenger?: boolean;
    shuttleDates: TTimestamp[];
    isActive?: boolean;
    checked?: boolean;      // Is checked needed anymore.  I think checked was replaced by selected.
    selected?: boolean;
    sessionId?: number;
    disabled?: boolean;
    uid: string;
}
export type TCustomer = ICustomer;

export interface Icoordinate {
    latitude?: number;
    longitude?: number;
}

export interface IIsoCountry {
    countryName: string;
    isoCountryCode: string;
    countryAbbr: string;
    phonePrefixes: string[];
    regions: TRegion[];
    uid?: string;
}
export type TIsoCountry = IIsoCountry;

export interface IIsoCountryRawData {
    label: string;
    isoCountryCode: string;
    latitude: number;
    longitude: number;
}
export type TIsoCountryRawData = IIsoCountryRawData;

export interface ISubDivisionsRawData {
    parentValue: string;
    value: string;
    caption: string;
    label: string;
    groupCaption: string;
    groupLabel: string;
}
export type TSubDivisionsRawData = ISubDivisionsRawData;

export interface ITimezoneRawData {
    parentValue: string;
    value: string;
    caption: string;
    label: string;
    groupCaption: string;
    groupLabel: string;
}
export type TTimezoneRawData = ITimezoneRawData;

export interface IIsoTimezone {
    offset: string;
    timezoneName: string;
    caption: string;
}
export type TIsoTimezone = IIsoTimezone;

export interface IIsoTimezones {
    timezones: TIsoTimezone[];
    createdDate?: TTimestamp;
    uid: string;
}
export type TIsoTimezones = IIsoTimezones;

export interface IChartData {
    dailyValues: number[],
    runningTotalValues: number[],
    labels: string[],
    // backgroundColors: [],
    // borderColors: any[]
    backgroundColors: unknown[],
    borderColors: unknown[]
}
export type TChartData = IChartData;
// export const NullChartData = {
//     dailyValues: [0],
//     runningTotalValues: [0],
//     labels: [''],
//     backgroundColors: [0],
//     borderColorsyValues: [0],

// };

export interface ICurrentId {
    incentiveSetId: number;
}
export type TCurrentId = ICurrentId;
export const NullCurrentId: TCurrentId = { 'incentiveSetId': -1 };

export interface IDailyValue {
    date: TTimestamp;
    count: number;
    campaignUids?: string[];
    customerUids?: string[];
    ticketUids?: string[];
    metadata: any;
    trips?: [{
        tripUid?: string;
        ticketUids?: string[];
    }];
}
export type TDailyValue = IDailyValue;

export interface IDailyValueDoc {
    date: TTimestamp;
    count: number;
    campaignUids?: string[];
    customerUids?: string[];
    ticketUids?: string[];
    trips?: [{
        tripUid?: string;
        ticketUids?: string[];
    }];
}
export type TDailyValueDoc = IDailyValueDoc;

// export type TEmailType = 'text' | 'html';
export interface IEmail {
    to?: string;
    from?: string;
    subject: string;
    htmlBody?: string;
    textBody?: string;
}
export type TEmail = IEmail;

export interface IFirestoreObject {
    // In this example, FirestoreObject is an interface that describes objects with
    // a uid property and any number of other properties. The uid property is
    // optional (uid?), because it might not exist before you assign a value to it.
    uid: string;
    [key: string]: unknown   // was any
}
export type TFirestoreObject = IFirestoreObject;

export interface IFunnelrUserFormField {
    formFieldId: string;
    value: string;
}
export type TFunnelrUserFormField = IFunnelrUserFormField;

export interface IFunnelrTagField {
    tagId: string;
    isRevoke: boolean;
}
export type TFunnelrTagField = IFunnelrTagField;

export interface IFunnelrListField {
    listId: string;
    unsubscribeReasonKey?: string;
    isRevoke: boolean;
}
export type TFunnelrListField = IFunnelrListField;

// 'listId': '3fa85f64-5717-4562-b3fc-2c963f66afa6',
// 'unsubscribeReasonKey': 'Inappropriate',
// 'isRevoke': true

export interface IFunnelrUser {
    email: string;
    firstName: string;
    lastName: string;
    telephone?: string;
    city?: string;
    timeZoneKey?: string;
    isoCountryCode?: string;
    isStaff: boolean;
    postalCode?: string;
    street?: string;
    isoRegionCode?: string;
    password: string;
    username: string;
    company?: string;
    statusId?: string;
    statusName?: string;
    userProfile: TFunnelrUserFormField[];
    tags?: TFunnelrTagField[];
    lists?: TFunnelrListField[];
    userUid?: string;
    userGuid?: string;
    userId?: string;
}

export type TFunnelrUser = IFunnelrUser;
export interface ISummaryCampaignDailyValues {
    count: number;
    date: TTimestamp;
    campaignUids: string[];
}
export type TSummaryCampaignDailyValues = ISummaryCampaignDailyValues;

export interface ISummaryCustomerDailyValues {
    count: number;
    date: TTimestamp;
    customerUids: string[];
}
export type TSummaryCustomerDailyValues = ISummaryCustomerDailyValues;

export interface ISummaryTicketDailyValues {
    count: number;
    date: TTimestamp;
    uids: string[];
    metadata: {
        serviceAdvisor: string;
        createdDate: TTimestamp;
    }
}
export type TSummaryTicketDailyValues = ISummaryTicketDailyValues;

export interface ISummaryTripInfo {
    completedDate: TTimestamp;
    driverFullName: string;     // was just driver
    tripUid: string;
    ticketUids: string[];
}
export type TSummaryTripInfo = ISummaryTripInfo;

export interface ISummaryTripDailyValues {
    count: number;
    date: TTimestamp;
    // trips: TSummaryTripInfo[];
    uids: string[];
    metadata: {
        driverFullName: string;     // was just driver
        completedDate: TTimestamp;
    }
}
export type TSummaryTripDailyValues = ISummaryTripDailyValues;


// export interface ISummarySmsInfo {
//     tripUid: string;
//     ticketUids: string[];
// }
// export type TSummarySmsInfo = ISummarySmsInfo;

export interface ISummarySmsDailyValues {
    count: number;
    date: TTimestamp;
    smsUids: string[]   // smsUidsTSummarySmsInfo[];
}
export type TSummarySmsDailyValues = ISummarySmsDailyValues;


export interface ISummaryCampaignDoc {
    dailyValues: TSummaryCampaignDailyValues[];
    uid: string;
}
export type TSummaryCampaignDoc = ISummaryCampaignDoc;

export interface ISummaryCustomerDoc {
    dailyValues: TSummaryCustomerDailyValues[];
    uid: string;
}
export type TSummaryCustomerDoc = ISummaryCustomerDoc;

export interface ISummaryTicketDoc {
    dailyValues: TSummaryTicketDailyValues[];
    uid: string;
}
export type TSummaryTicketDoc = ISummaryTicketDoc;

export interface ISummaryTripDoc {
    dailyValues: TSummaryTripDailyValues[];
    uid: string;
}
export type TSummaryTripDoc = ISummaryTripDoc;

export interface ISummarySmsDoc {
    dailyValues: TSummarySmsDailyValues[];
    uid: string;
}
export type TSummarySmsDoc = ISummarySmsDoc;

export type TSummaryDocs = 'campaignsSummary' | 'customersSummary' | 'ticketsSummary' | 'tripsSummary' | 'smsSummary';

// export interface IDataResult {
//     status?:     boolean;
//     message?:    string;
//     // data?:       any;
//     data?:       unknown;       // TODO: 2/24/2024  This is a hack to get around the fact that the data is not typed.  It
//     length?:     number;
// }

export interface IDealership {
    address: TAddress;
    dealershipName?: string;
    phoneNumber: string;
    uid: string;
}
export type TDealership = IDealership;

export interface IDistance {
    kilometers: number;
    miles: number;
}
export type TDistance = IDistance;

export interface IDistinctColorInfo {
    name: string;
    hexColor: string;
}
export type TDistinctColorInfo = IDistinctColorInfo;

export interface IDocument {
    documentName: string;
    domain: string;
    viewName: string;
    contents: string;  // added ? 2/24/2024
    uid: string;        // added ? 2/24/2024
}
export type TDocument = IDocument;

export interface IDomainInfo {
    domain: string;
    domainWithPort: string;
    origin: string;
    properOrigin: string;      // will never be localhost.  localhost is replaced with staging!
}
export type TDomainInfo = IDomainInfo;
// export type TDomain = 'localhost' | 'staging' | 'app';  // prod
export type TDomain = 'staging' | 'app';  // prod

// export interface IDriver {
//     firstName?: string;
//     fullName?: string;
//     lastName?: string;
//     uid?: string;
// }

export interface IEnvironment {
    production: boolean;
    firebase: {
        projectId: string;
        appId: string;
        databaseURL: string;
        storageBucket: string;
        locationId: string;
        apiKey: string;
        authDomain: string;
        messagingSenderId: string;
        measurementId: string;
    };
    googleMapsApiKey: string;
    recaptchaSiteKey: string;
}
export type TEnvironment = IEnvironment;


export interface IError {
    errDate: string;
    errorMessage?: string;
    stack?: string;
    errMsg2?: string;
    fileName?: string;
    lineNumber?: string;
    columnNumber?: string;
    accountNumber?: string;
    usersFullName?: string;
    usersEmail?: string;
    internetConnectivityStatus?: string;
    uid?: string;
}
export type TError = IError;

// Step 1) Keep the following data in the event node
// export interface IEvent {
//     accountNumber?: string;  // who?: account
//     userEmail?: string;      // who?: user
//     eventDate?: number;      // when
//     eventType?: string;      // what?: Signup, Login, WatchedVideo, SetupAccountProfile, TicketCompleted, etc
//     eventTag?: string;       // VideoId, ExpirationDate
// }

export interface IGeocoderResult {
    requestedAddress?: string;       // TRequestedAddress;
    result?: {
        formattedAddress?: string;
        coordinate?: TCoordinate;
    };
    statusMsg?: string;
}
export type TGeocoderResult = IGeocoderResult;

export interface IInternetConnection {
    isConnected?: boolean;
}
export type TInternetConnection = IInternetConnection;

export interface ILastPaymentInfo {
    paymentDate?: TTimestamp;
    amount?: number;
    reason?: string;
}
export type TLastPaymentInfo = ILastPaymentInfo;

export interface ILoginCredentials {
    email?: string;
    password?: string;
    userUid?: string;
}
export type TLoginCredentials = ILoginCredentials;

export interface IMarkerOptions {
    position?: unknown,
    icon?: string,
    // animation?: any,
    animation?: unknown,
    title?: string,
    duration?: number,
    easing?: string,
    // map?: any,
    map?: unknown,
    shuttleName?: string
}
export type TMarkerOptions = IMarkerOptions;

export interface INetPromoterScore {
    createdDate?: TTimestamp;
    nps?: number;
    question?: string;
    reason?: string;
    suggestions?: string;
    uid?: string;
}
export type TNetPromoterScore = INetPromoterScore;
export interface IOrder {
    billing: TOrderBilling;
    header: TOrderHeader;
    items: TOrderItem[];
    shipping: TOrderShipping;
    uid?: string;
}
export type TOrder = IOrder;

export interface IOrderBilling {
    accountNumber?: string;
    address1?: string;
    address2?: string;
    city?: string;
    company?: string;
    country?: string;
    email?: string;
    phone?: string;
    postcode?: string;
    regionName?: string;
    firstname?: string;
    lastname?: string;
}
export type TOrderBilling = IOrderBilling;

export interface IOrderHeader {
    currency?: string;
    createdDate?: {
        date: TTimestamp;
        timezone: string;
        timezone_type: number;
    };
    createdDateFormatted?: TTimestamp;
    description?: string;
    discountTotal?: number;
    notes?: string;
    orderId?: number;
    paymentMethod?: string;
    paymentMethodTitle?: string;
    shippingTotal?: number;
    status?: string;
    total: number;
    totalFormatted: string;
    totalTax?: number;
    version?: string;
}
export type TOrderHeader = IOrderHeader;

export interface IOrderShipping {
    firstName?: string;
    lastName?: string;
    company?: string;
    address1?: string;
    address2?: string;
    city?: string;
    regionName?: string;
    postcode?: string;
    country?: string;
}
export type TOrderShipping = IOrderShipping;

export interface IOrderItem {
    // price?: number;
    // priceFormatted?: string;
    name: string;
    quantity: number;
    sku: string;
    subtotal: number;
    subtotalFormatted?: string;
    tax: number;
    taxFormatted?: string;
    total: number;
    totalFormatted?: string;
    type: string;
    unitPrice: number;
    unitPriceFormatted?: string;
}
export type TOrderItem = IOrderItem;

export type TPeriod = 'days' | 'months' | 'years';

export type TPickupStatus = 'findShuttles' | 'sendRequest' | 'responseReceived' | 'waitAndSupplyDefaultDeclinedResponse' | 'quit';

export interface IPickupRequest {
    ticket?: TTicket;
    // promises?: any[];
    promises?: unknown[];
    closestShuttles?: TShuttle[];
    status?: TPickupStatus;
    // responses?: any[];
    responses?: unknown[];
    currentIdx?: number;
    waitingCount?: number;
    // availableShuttlesCallback?: any;
    availableShuttlesCallback?: unknown;
    createdDate?: TTimestamp;
    shuttleUid?: string;
    uid?: string;
}
export type TPickupRequest = IPickupRequest;

export interface IPickupResponse {
    ticket?: TTicket;
    ticketUid?: string;
    requestUid?: string;
    accepted?: boolean;
    createdDate?: TTimestamp;
    shuttleUid?: string;
    uid?: string;
}
export type TPickupResponse = IPickupResponse;
// 'shuttleUid': shuttleUid, 'ticketUid': ticket.uid, 'ticket': ticket,
// 'requestUid': requestUid, 'accepted': accept, 'uid': shuttleUid

// export interface IPirateEvent {
//     accountNumber?: string;
//     createdDate?: number;
//     tag?: string;
//     type?: string;
//     userEmail?: string;
// }
// export type TPirateEvent = IPirateEvent;

export interface IPopoverMenuItem {
    name: string;
    testId: string;
    content: string;
}
export type TPopoverMenuItem = IPopoverMenuItem;

// export interface IPositionTracker {
//     subscribe(onNewPosition?: (coords?: Icoordinate) => void): void;
// }

export interface IPost {
    id?: number;
    title?: string;
    subtitle?: string;
    excerpt?: string;
    imageUrl?: string;
    uid?: string;
    categories?: string[];
}
export type TPost = IPost;

export interface IRandomWords {
    numbers: string[];
    colors: string[];
    adjectives: string[];
    nouns: string[];
}
export type TRandomWords = IRandomWords;

// export interface IRegistrationData {
//     affiliateId?: string;
//     countryName?: string;
//     countryCode?: string;
//     incentiveCode?: string;
//     dealershipName?: string;
//     email?: string;
//     firstName?: string;
//     lastName?: string;
//     role?: string;
//     regionName?: string;
//     regionCode?: string;
// }
// export type TRegistrationData = IRegistrationData;

export interface IReportType {
    'title'?: string;
    'description'?: string;
    'name'?: string;
    'columns'?: string[];
    'sortColumns'?: number[];
    'colWidths'?: number[];
    'reportParmsNeeded'?: boolean;
}
export type TReportType = IReportType;

export interface IReport {
    // type?: string;
    // createReportService(reportOptions?: TReportOptions): any;
    createReportService(reportOptions?: TReportOptions): unknown;
}
export type TReport = IReport;

export interface IReportData {
    'data'?: string[];
}
export type TReportData = IReportData;

export interface IReportOptions {
    selectedReport: IReportType;
    startingDate: TTimestamp;
    endingDate: TTimestamp;
}
export type TReportOptions = IReportOptions;

export interface IGetReportData {
    getReportData(): Promise<TReportData>;

}

// export type TRequestedAddress?: string;

// export interface IRequestedAddress {
//     streetAddress?: string;
// }
// export type TRequestedAddress = IRequestedAddress;

// export declare type RunGuardsAndResolvers = 'pathParamsChange' | 'pathParamsOrQueryParamsChange' |
//   'paramsChange' | 'paramsOrQueryParamsChange' | 'always' |
//   ((from?: ActivatedRouteSnapshot, to?: ActivatedRouteSnapshot) => boolean);


export declare type TRole = 'accountant' | 'driver' | 'manager' | 'marketer'| 'serviceAdvisor' | 'undefined';

export type TRoleInfo = {
    title: string;
    role: TRole
};

export type TRootSessionData = {
    accountProfile: TAccountProfile;
    dealership: TDealership;
    settings: TSetting;
}

// export interface IRouteOld {
//     dealership?: TDealership;
//     endingPoint?: TRoutePoint;
//     modifiedDate?: number;
//     routeLegs?: TRouteLeg[];
//     selectedTickets?: TTicket[];
//     shuttle?: TShuttle;
//     startingPoint?: TRoutePoint;
//     uid?: string;
// }
// export type TRoute = IRouteOld;

// TODO: In IRoute, remove all the ? from the fields.
export interface IRoute {
    endingPoint: TRoutePoint;
    routeLegs: TRouteLeg[];
    createdDate?: TTimestamp;
    selectedTickets?: TTicket[];
    shuttle: TShuttle;
    startingPoint: TRoutePoint;
    uid: string;
}
export type TRoute = IRoute;

export interface IRouteLeg {
    from?: TRoutePoint;
    isUrgent?: boolean;
    letter?: string;
    to?: TRoutePoint;
    passengerName?: string;
    status?: string;
    ticketUid?: string;
    waypointNumber?: number;
}
export type TRouteLeg = IRouteLeg;

export interface ILocation {
    address: TAddress;
    locationName?: string;
}
export type TLocation = ILocation;

export interface IRoutePoint {      // Mar 22. all fields have ?.  I removed all ?
    location: TLocation;
    atDealership?: boolean;
    fromDealership?: boolean;
    driverName?: string;
    passengerName?: string;
    letter?: string;
    waypointNumber?: number;
    allAboardRenderedMessage?: string;
}
export type TRoutePoint = IRoutePoint;

export interface IServiceArea {
    city: string;
    distance: number;
    regionCode: string;
    regionName: string;
    zipcode: string;
    uid?: string;
}
export type TServiceArea = IServiceArea;

export interface ISession {
    email: string;
    firstName: string;
    lastName: string;
    loginDate?: TTimestamp;
    role: string;
    userUid: string;
}
export type TSession = ISession;

export interface IAllAboard {
    enabled: boolean;
    numMinutes: number;
    rawMessage: string;
    renderedMessage?: string;
}
export type TAllAboard = IAllAboard;

export interface IApproachingDropoff {
    enabled: boolean;
    numMilesToGo: number;
    rawMessage: string;
    renderedMessage?: string;
}
export type TApproachingDropoff = IApproachingDropoff;

export interface IApproachingPickup {
    enabled: boolean;
    numMilesToGo: number;
    rawMessage: string;
    renderedMessage?: string;
}
export type TApproachingPickup = IApproachingPickup;

export interface IHosting {
    enabled: boolean;
    campaignHostingLink: string;
    enabledStr?: string;
}
export type THosting = IHosting;

export interface ICampaignMessage {
    enabled: boolean;
    enabledStr?: string;
    rawMessage: string;
    renderedMessage: string;
    message: string;
}
export type TCampaignMessage = ICampaignMessage;

export interface IViewModelMessages {
    enabled: boolean;
    enabledStr?: string;
    rawMessage: string;
    renderedMessage: string;
    message: string;
}
export type TViewModelMessages = IViewModelMessages;

export interface IPickupArrived {
    enabled: boolean;
    rawMessage: string;
    renderedMessage?: string;
}
export type TPickupArrived = IPickupArrived;

export interface IRideCompleted {
    enabled: boolean;
    rawMessage: string;
    renderedMessage?: string;
}
export type TRideCompleted = IRideCompleted;

export interface ITicketCreated {
    enabled: boolean;
    rawMessage: string;
    renderedMessage?: string;
}
export type TTicketCreated = ITicketCreated;

export interface IMisc {
    showNearbyAddress?: boolean;
    showShuttleDistance?: boolean;
    showSimulationButton?: boolean;
    saveNavigationLocationFrequency?: number;
    showTrackerModeButton: boolean;
    quickLogin?: boolean;
}
export type TMisc = IMisc;

export type TMessageType = 'warning' | 'info' | 'action' | 'announcement';
export type TMessagePriority = 'urgent' | 'high' | 'normal' | 'low';
export type TMessageStatus = 'unread' | 'read' | 'dismissed';

export interface IMessage {
    uid: string;
    title: string;
    summary: string;
    body: string;
    type: TMessageType;
    priority: TMessagePriority;
    status: TMessageStatus;
    createdDate: TTimestamp | Date;
    expirationDate?: TTimestamp | Date;
    actionLabel?: string;
    actionUrl?: string;
    icon?: string;
    // Targeting: Who should see this message?
    // If both are empty/undefined, message is visible to ALL users in the account.
    audience?: TRole[];          // Role-based targeting (e.g., ['manager'] or ['driver', 'serviceAdvisor'])
    recipientEmail?: string;     // Individual targeting - specific user's email address
}
export type TMessage = IMessage;

// ================================================================
// Reward Funnel Types
// ================================================================

/**
 * The type of condition to evaluate for a reward funnel rule.
 * - fieldExists: Check if a specific field has a non-empty value in a Firestore document
 * - fieldNotExists: Check if a specific field does NOT exist or is empty in a Firestore document
 * - collectionNotEmpty: Check if a Firestore collection has at least minCount documents
 * - expirationWithinDays: Check if the account's expiration date is within N days
 * - accountStatus: Check if the account status matches a specific value
 * - custom: A named custom condition handled by the service (e.g., testimonial submitted)
 */
export type TRewardFunnelConditionType = 'fieldExists' | 'fieldNotExists' | 'collectionNotEmpty' | 'expirationWithinDays'
    | 'accountStatus' | 'custom';

export interface IRewardFunnelCondition {
    type: TRewardFunnelConditionType;
    collection?: string;        // e.g., 'users', 'shuttles', 'serviceAreas'
    document?: string;          // e.g., 'dealershipProfile' (for single-doc checks)
    field?: string;             // e.g., 'dealershipName' (for fieldExists)
    minCount?: number;          // e.g., 1 (for collectionNotEmpty)
    days?: number;              // e.g., 5 (for expirationWithinDays)
    statusValue?: string;       // e.g., 'In Trial Period' (for accountStatus)
    customName?: string;        // e.g., 'testimonialSubmitted' (for custom)
}
export type TRewardFunnelCondition = IRewardFunnelCondition;

/**
 * The type of reward to apply when a rule is satisfied.
 * - extendExpiration: Add N days to the account's expiration date
 * - couponCode: Apply a coupon code to reduce the monthly/yearly price
 * - messageOnly: No reward — just send a message (used for alerts/warnings)
 */
export type TRewardFunnelRewardType = 'extendExpiration' | 'couponCode' | 'messageOnly';

export interface IRewardFunnelReward {
    type: TRewardFunnelRewardType;
    days?: number;              // For extendExpiration
    couponCode?: string;        // For couponCode (discounts will be applied at checkout time based on the code)
    description?: string;       // Human-readable reward description
}
export type TRewardFunnelReward = IRewardFunnelReward;

/**
 * The message template to create in the Message Center when a rule fires.
 */
export interface IRewardFunnelMessageTemplate {
    title: string;
    summary: string;
    body: string;
    type: TMessageType;
    priority: TMessagePriority;
    actionLabel?: string;
    actionUrl?: string;
    icon?: string;
    audience?: TRole[];          // Role-based targeting (e.g., ['manager'] or ['driver', 'serviceAdvisor'])
}
export type TRewardFunnelMessageTemplate = IRewardFunnelMessageTemplate;

/**
 * A single rule in the reward funnel configuration.
 */
export interface IRewardFunnelRule {
    id: string;                             // Unique rule identifier (e.g., 'onboarding-dealership')
    name: string;                           // Human-readable name
    category: 'onboarding' | 'alert' | 'promotion' | 'milestone';
    enabled: boolean;                       // Can disable rules without removing them
    condition: TRewardFunnelCondition;       // What triggers this rule
    reward: TRewardFunnelReward;            // What reward to apply (can be messageOnly)
    message: TRewardFunnelMessageTemplate;  // Message to create in Message Center
    oneTime: boolean;                       // If true, only fires once per account
    sortOrder?: number;                     // Display/evaluation order
    dependsOn?: string[];                   // Rule IDs that must have fired before this one
    appliesTo?: TAccountStatus[];           // Only evaluate for these account statuses
}
export type TRewardFunnelRule = IRewardFunnelRule;

/**
 * Configuration object loaded from rewardFunnel.config.json.
 */
export interface IRewardFunnelConfig {
    version: string;
    rules: TRewardFunnelRule[];
}
export type TRewardFunnelConfig = IRewardFunnelConfig;

/**
 * Tracks which rules have already been applied to a specific account.
 * Stored in Firestore at: accounts/{accountNumber}/rewardFunnel/status
 */
export interface IRewardFunnelAccountStatus {
    accountNumber: string;
    firedRules: TRewardFunnelFiredRule[];
    lastEvaluatedDate: TTimestamp | Date;
    uid?: string;
}
export type TRewardFunnelAccountStatus = IRewardFunnelAccountStatus;

/**
 * A record of a rule that has already fired for an account.
 */
export interface IRewardFunnelFiredRule {
    ruleId: string;
    firedDate: TTimestamp | Date;
    messageUid?: string;        // Reference to the message created in the Message Center
    rewardApplied?: string;     // Human-readable description of the reward given
}
export type TRewardFunnelFiredRule = IRewardFunnelFiredRule;

export type TResponse = {
    // selection?: boolean;
    okay: boolean;
    selectedItems: string[];
}

export interface ISetting {
    ticketCreated: TTicketCreated;
    allAboard: TAllAboard;
    dropoffApproaching: TApproachingDropoff;
    dropoffCompleted: TRideCompleted;
    pickupApproaching: TApproachingPickup;
    pickupArrived: TPickupArrived;
    pickupCompleted: TRideCompleted;
    hosting: THosting;
    campaignMessage: TCampaignMessage;
    misc?: TMisc;
    modifiedDate?: TTimestamp;
    uid?: string;
}
export type TSetting = ISetting;

export interface IShuttle {
    shuttleName: string;
    description: string;
    driverName: string;
    color: string;
    status: TShuttleStatus;
    seatsAvailable: number;
    passengerCapacity: number;
    vector: TVector;
    uid: string;
}
export type TShuttle = IShuttle;
export type TShuttleStatus = 'Idle' | 'Assigned' | 'Traveling';

export interface ISkuAction {
    sku: string;
    description: string;
    increaseExpiration: boolean;            // if true, bump up expiration days
    increaseExpirationDays: number;         // if < 1, don't touch expiration date
    increaseExpirationMonths: number;       // if < 1, don't touch expiration date
    increaseExpirationYears: number;        // if < 1, don't touch expiration date
    increasePremiumSupportMonths: number;   // if < 1, don't touch premium support expiration date
    increasePremiumSupportYears: number;    // if < 1, don't touch premium support expiration date
    increaseWebHostingMonths: number;       // if < 1, don't touch web hosting expiration date
    increaseWebHostingYears: number;        // if < 1, don't touch web hosting expiration date
    updateActivationInfo: boolean;
    updateCredits: boolean;
    updatePackageDetails: boolean;
    sendCompleteOrderEmail: boolean;
    type: string;
    uid: string;
}
export type TSkuAction = ISkuAction;

export interface ISmsSentStatus {
    sequenceNo: number;
    passengerName: string;
    cellPhone?: string;
    maskedCellPhone?: string;
    sentStatus: TSentStatus;
    sentDate: TTimestamp | '';
    ticket: TTicket;
    sessionId?: number;
}
export type TSmsSentStatus = ISmsSentStatus;
export type TSentStatus = 'pending' | 'sent';

export interface ISmsFromNumber {
    fromNumber: string;
    numAccounts: number;
    uid: string;
}
export type TSmsFromNumber = ISmsFromNumber;

export interface ISmsSentMessage {
    // accountNumber?: string;
    // bodyText?: string;
    cellPhone?: string;
    countryCode?: string;
    customerName?: string;
    customerReceivedMessageDate?: TTimestamp;
    fromNumber?: string;
    message?: string;
    numSegments?: number;
    sentToCustomerDate?: TTimestamp;
    sentToTwilioDate?: TTimestamp;
    receivedAt?: TTimestamp;
    sentDate?: TTimestamp;
    sentAt?: TTimestamp;
    sessionId?: number;
    smsType?: TSmsType;
    toNumber?: string;
    uri?: string;
    uid: string;
}
export type TSmsSentMessage = ISmsSentMessage;

export type TSmsType = 'smsTicketCreated' | 'smsAllAboard' | 'smsDropoffApproaching' |
    'smsDropoffCompleted' | 'smsPickupApproaching' | 'smsPickupArrived' |
    'smsPickupCompleted' | 'smsCampaign' | 'smsTest';
export interface ISpeed {
    kph?: number;
    mph?: number;
}
export type TSpeed = ISpeed;

export interface IRegion {
    'regionName': string;
    'regionCode': string;
}
export type TRegion = IRegion;

export interface IRegionDocument {
    state: string;
    statecode: string;
    countrycode: string;
    timezone: string;
    zipcodes: TZipcode[];
}
export type TRegionDocument = IRegionDocument;


export interface ISummary {
    'numCampaigns'?: number,
    'numCustomers': number,
    'numRoutes'?: number,
    'numServiceAreas'?: number,
    'numShuttles'?: number,
    'numTickets'?: number,
    'numTrips'?: number,
    'numUsers'?: number,

    'smsTicketCreated'?: number;
    'smsAllAboard'?: number;
    'smsDropoffApproaching'?: number;
    'smsDropoffCompleted'?: number;
    'smsPickupApproaching'?: number;
    'smsPickupArrived'?: number;
    'smsPickupCompleted'?: number;

    'dataUsage'?: {
        'campaigns'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'customers'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'expirationDateExtension'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'serviceAreas'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'shuttles'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsCampaigns'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsTicketCreated'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsAllAboard'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsDropoffApproaching'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsDropoffCompleted'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsPickupApproaching'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsPickupArrived'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'smsPickupCompleted'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'tickets'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'tracker'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'trips'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
        'users'?: {
            count?: number,
            lastModifiedDate?: TTimestamp
        };
    };
    'viewUsage'?: {
        'expirationExtension'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsTicketCreated'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsAllAboard'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsApproachingDropoff'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsDropoffCompleted'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsApproachingPickup'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsPickupArrived'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsPickupCompleted'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'settingsCampaign'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'navigation'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'routes'?: {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'tickets': {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
        'tracker': {
            count?: number,
            lastAccessedDate?: TTimestamp
        };
    };
    uid?: string;
}
export type TSummary = ISummary;

export type TSummaryType = 'campaignsSummary' | 'customersSummary' | 'overallSummary' | 'smsSummary' | 'ticketsSummary' | 'tripsSummary';
export interface ITask {
    header?: {
        taskType?: TTaskType;
        taskCreatedDate?: TTimestamp;    // Can't use timestamp.  It doesn't support milliseconds.
        // taskCreatedDate?: number;
        // executeWhen?: TTaskExecuteWhen;
        // executeWhen?: Timestamp;
        executeWhen?: number;
        accountNumber?: string;
        countryCode?: string;
        dealershipPhoneNumber?: string;
        domain?: string;
    };
    textMessageData?: {
        customer?: TCustomer;
        smsType?: TSmsType;
        textMessage?: string;
    };
    ticketData?: {
        ticketUid?: string;
        executeWhen?: number;
    };
    // registrationData?: TRegistrationData;
    uid?: string;
}
export type TTask = ITask;

export type TTaskType = 'sendText' | 'updateTicketStatus' | 'createAccount';
// export type TTaskExecuteWhen = 'asap' | 'schedule';
// export interface IVariables {       // Text Message Replacement Variables
//     accountNumber?: string;         // newTextMessage = newTextMessage.replace('[AccountNumber]', accountNumber);
//     campaignLink?: string;          // newTextMessage = newTextMessage.replace('[CampaignLink]', newCampaignLink);
//     customer?: TCustomer;           // message = message.replace('[Name]', customer.name);
//     currentTime?: string;           // message = message.replace('[CurrentTime]', now);
//     customerName?: string;          // newTextMessage = newTextMessage.replace('[CustomerName]', customer.customerName);
//     dealershipName?: string;        // message = message.replace('[DealershipName]', dealershipName);
//     departureTime?: string;         // message = message.replace('[DepartureTime]', fromNow);
//     domain?: string;                // let newTextMessage = textMessage.replace('[Domain]', domain);
//     fakeCustomer?: TCustomer;       //
//     fakeDepartureTime?: string;     //
//     hostingLink?: string;           // let newTextMessage = textMessage.replace('[Domain]', domain);
//     numMilesToGo?: string;          // message = message.replace('[NumMilesToGo]', numMilesToGo.toString());
//     numMinutes?: string;            // message = message.replace('[NumMinutes]', numMinutes.toString());
//     numMiles?: number;              // message = message.replace('[NumMiles]', distance.miles.toString());
//     optoutLink?: string;            // newTextMessage = newTextMessage.replace('[OptoutLink]', newOptoutLink);
//     shuttleDate?: number;           // newTextMessage = newTextMessage.replace('[ShuttleDate]', shuttleDate);
//     shuttleDate?: number;           // newTextMessage = newTextMessage.replace('[ShuttleDate]', shuttleDate);
//     shuttleDate?: TTimestamp;       // newTextMessage = newTextMessage.replace('[ShuttleDate]', shuttleDate);
//     toCoordinate?: TCoordinate;
//     currentCoordinate?: TCoordinate;
//     debugData?: any;
// }
// export type TVariables = IVariables;

export interface ITicket {
    // address?: string;    // address is now in dropoff and pickup
    allAboardDate?: TTimestamp;
    assignedDate?: TTimestamp;
    assignedDateStr?: string;
    attendance?: boolean;
    attendanceStr?: string;
    cellPhone: string;
    completedDate?: TTimestamp;
    // coordinate?: TCoordinate;
    createdDate?: TTimestamp;
    currentDate?: TTimestamp;
    serviceAdvisor?: string;
    driver?: string;
    dropoff?: TLocation;
    enrouteDate?: TTimestamp;
    // fromAddress?: string;
    fromDealership?: boolean;
    isUrgent?: boolean;
    // locationName?: string;
    note?: string;
    numPassengers?: number;
    numPassengersStr?: string;
    passengerName: string;
    pickup?: TLocation;
    priority?: boolean;
    priorityStr?: string;
    status?: TTicketStatus;
    selected?: boolean;
    takeBelongingsDate?: TTimestamp;
    // toAddress?: string;
    uid: string;
}
export type TTicket = ITicket;

export declare type TTicketStatus = 'pending' | 'assigned' | 'enroute' | 'current' | 'completed';
export declare type TTripType = 'Actual' | 'Simulated';

export interface ITrip {
    actualLocations?: TTripActualLocationDoc[];
    driver: string;                // #ITrip-driver-expected-to-be-string
    endTripDate: TTimestamp;
    startTripDate: TTimestamp;
    shuttle: TShuttle;
    tickets: TTicket[];
    plannedRoute?: TRoute;
    status: TTripStatus;
    totalPositions?: number;
    type: TTripType;
    uid: string;
}
export type TTrip = ITrip;
export declare type TTripStatus = 'driving' | 'completed';

export interface ITripActualLocation_Original {
    dateTime?: TTimestamp;
    coordinate?: TCoordinate;
    legIndex?: number;
    sequenceNum?: number;
    speed?: TSpeed;
    uid?: string;
}
export type TTripActualLocation_Original = ITripActualLocation_Original;

export interface ITripActualPosition {
    dateTime: TTimestamp;
    coordinate: TCoordinate;
    speed: TSpeed;
    // heading: any;
    heading: unknown;
    legIndex: number;
}
export type TTripActualPosition = ITripActualPosition;

export interface ITripActualLocationDoc {
    actualPositions: TTripActualPosition[];
    legIndex?: number;
    sequenceNum?: number;
    uid?: string;
}
export type TTripActualLocationDoc = ITripActualLocationDoc;


export interface IUser {
    firstName: string;
    lastName: string;
    cellPhone: string;
    email: string;
    role: TRole;
    roleTitle?: string;
    uid: string;
}
export type TUser = IUser;

export interface IUserAccountMap {
    'accountNumber': string;
    'createdDate': TTimestamp;
    'email': string;
    'modifiedDate'?: TTimestamp;
    'pwd'?: string;
    'uid': string;
    'funnelrUserGuid'?: string;
    'funnelrUserUid'?: string;

}
export type TUserAccountMap = IUserAccountMap;

export type TValidation = {
    'requestedAddress': TAddress;
    'location': {
        'formattedAddress': string;
        'locationType': 'ROOFTOP' | 'RANGE_INTERPOLATED' | 'GEOMETRIC_CENTER' | 'APPROXIMATE';
        'coordinate': {
            'latitude': number;
            'longitude': number;
        }
    },
    'foundAddress': string;
    'found': boolean;
    'foundZipcode': string;
    'foundCity': string;
    'statusTitle': string;
    'statusMessage': string;
    'streetNamePartialMatch': boolean;
    'zipcodePartialMatch': boolean;
};

export declare type TValidator = 'text' | 'optphone' | 'number' | 'email' | 'phone';

export interface IVector {
    accuracy: string;
    currentLocation: TCoordinate;
    currentTime: TTimestamp;
    distanceFromDealership: TDistance;
    distanceToTicket: TDistance;
    heading: unknown;
    modifiedDate: TTimestamp;
    nearAddress: string;
    previousLocation: TCoordinate;
    previousTime: TTimestamp;
    speed: TSpeed;
    tripUid: string;
    uid: string;
}
export type TVector = IVector;
export declare type TVectorType = 'real' | 'simulated';

export interface IVideo {
    audience?: string[];
    description?: string;
    sequenceNumber?: number;
    title?: string;
    // trustedUrl?: any;
    trustedUrl?: unknown;
    videoUrl?: string;
    uid?: string;
}
export type TVideo = IVideo;

export interface IZipcodes {
    regionName: string;
    regionCode: string;
    countrycode: string;
    numZipcodes: number;
    timezone: string;
    uid: string;
    zipcodes: TZipcode[];
}
export type TZipcodes = IZipcodes;

export interface IZipcode {
    city: string;
    coordinate: TCoordinate;
    county: string;
    zipcode: string;
}
export type TZipcode = IZipcode;

// ViewModels
export interface ITicketsViewModel {
    ticket: TTicket;

    step2Complete: boolean;
    isUrgencyStr: string;
    directionStr: string;
    addressVerified: boolean;
    addressVerifiedMsg: string;
    pickupFullAddressFoundMsg: string;
    dropoffFullAddressFoundMsg: string;

}
export type TTicketsViewModel = ITicketsViewModel;

// In your models file
export interface IAddressModalViewModel {
    title: string;
    optionLabel1: string;
    optionLabel2: string;
    dealershipLocation: TLocation;  // Keep for reference
    startingLocation: TLocation;    // Defaults to dealershipLocation
    endingLocation: TLocation;      // Defaults to dealershipLocation
    position: 'start' | 'end';
    addressVerified: boolean;
    startingSource: 'dealership' | 'customLocation';
    endingSource: 'dealership' | 'customLocation';
}
export type TAddressModalViewModel = IAddressModalViewModel;

// export interface IAddressModalViewModel {
//     title: string;
//     optionLabel1: string;
//     optionLabel2: string;
//     dealershipLocation: TLocation;
//     customStartingLocation?: TLocation | null;
//     customEndingLocation?: TLocation | null;
//     // source: string;
//     startingSource: string;
//     endingSource: string;
//     position: string;
//     addressVerified: boolean;
// }
// export type TAddressModalViewModel = IAddressModalViewModel;
export interface IRoutesViewModel {
    'dropoffManifest': { 'selectedTickets': TTicket[], 'seatsAvailable': number },
    'pickupManifest': { 'selectedTickets': TTicket[], 'seatsAvailable': number },
    'selectedTickets': TTicket[];
    'sentStatuses': TSmsSentStatus[];
    'selectedShuttle': TShuttle;
    'setting': TSetting;
    'startingPoint': TRoutePoint;
    'endingPoint': TRoutePoint;
    'routeLegs': TRouteLeg[];
    'numDropoffPassengers': number,
    'dealership': TDealership,
    'driverName': string,
    'sms': { 'allAboardRenderedMessage'?: string },
    'sessionId': number,
    'step1Complete': boolean,
    'step2Complete': boolean,
    'step3Complete': boolean,
    'step4Complete': boolean,
    'step5Complete': boolean,
    'step6Complete': boolean,
    'step7Complete': boolean,

}
export type TRoutesViewModel = IRoutesViewModel;

// Mar 22, every column had ?.  all ? removed.
export interface INavigationViewModel {
    assignedTickets: TTicket[];
    completedTickets: TTicket[];
    currentTickets: TTicket[];
    distanceFromDestination: TDistance,
    dropoffTickets: TTicket[];
    enrouteTickets: TTicket[];
    pickupTickets: TTicket[];
    shuttles: TShuttle[];
    selectedShuttle: TShuttle;
    route: TRoute;

    attendanceSummary: string;
    currentLegIdx: number;
    currentLeg: TRouteLeg;
    currentTicket: TTicket;
    isDebugMode?: boolean;
    myVector: TVector;
    numAttendees: number;
    numDropoffsStr: string;
    numEmptySeats: number;
    step1Complete: boolean;
    step2Complete: boolean;
    step3Complete: boolean;
    tickets: TTicket[];
    trip: TTrip
}
export type TNavigationViewModel = INavigationViewModel;

export interface IApplicationFormViewModel {
    // Manager Info
    'user': TUser;
    'questionsAndAnswers': TAccountCreationQuestionsAndAnswers;
    
    // firstName?: string;
    // lastName?: string;
    // cellPhone?: string;
    // email?: string;
    // role?: TRole;

    // // Qualification checkboxes
    // hasReliableInternet?: boolean;
    // locatedInUnitedStates?: boolean;
    // isServiceManager?: boolean;

    // // Trial terms checkboxes
    // acceptsTrialTerms?: boolean;
    // commitsToOnboarding?: boolean;
    // acceptsLearningCurve?: boolean;

    // Marketing/Attribution
    referralSource?: string;

    // Account creation response
    password?: string;
    claims?: string;

    // Other
    phonePrefixes?: string[];
    accountNumber?: string;
    affiliateId?: string;
    couponCode?: string;
}
export type TApplicationFormViewModel = IApplicationFormViewModel;

export interface IQueryOptions {
    collectionPath: string;
    orderByField?: string;
    orderByDirection?: OrderByDirection;
    limitCount?: number;
    startAt?: any;     // Added for range queries
    endAt?: any;       // Added for range queries
    whereFilters?: Array<{
        field: string;
        operator: WhereFilterOp;
        value: any;
    }>;
}
export type TQueryOptions = IQueryOptions;
